import { Form } from "@unform/web";
import { useEffect, useState } from "react";

import { HTTPErrorCallback } from "Helpers/Error";

import UseAuth from "Hooks/UseAuth";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import GetIcon from "Components/Global/Icons";

import GalleryUpload from "../GalleryUpload";
import * as S from "./styles";

import { useTheme } from "styled-components";

// Sessão ÁLBUM do projeto (B05 — D15–D18 da parte 2, 03/09/2026).
//
// Era "Galeria" e só aceitava imagem. Agora aceita a lista ampla (imagem,
// vídeo, áudio, PDF/Office/compactados) e LINK externo (YouTube/Vimeo), com o
// `type` vindo do backend. A exibição varia: imagem no lightbox, vídeo com
// player, link e arquivo como âncora.
//
// ⚠️ O lightbox navega SÓ entre imagens — com vídeo e PDF na mesma lista, as
// setas passariam por itens que ele não sabe renderizar.
const Gallery = ({ projectId }) => {
  const theme = useTheme();
  const { parsedJWT } = UseAuth();
  const { api } = UseServices();
  const { loading } = UseGlobalLoading();

  const [items, setItems] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    return getProjectImages(projectId);

    // eslint-disable-next-line
  }, []);

  const getProjectImages = (project_id) => {
    api
      .post("/projects/gallery", {
        project_id: project_id,
      })
      .then((response) => setItems(response.data || []))
      .catch((error) => {
        return HTTPErrorCallback(error);
      });
  };

  const is_sponsor = parsedJWT?.user?.is_sponsor;

  // Só as imagens entram no lightbox (ver nota do topo).
  const images = items.filter((item) => item.type === "image" || !item.type);

  const arrowLeftComponent = selectedImageIndex !== 0 && (
    <GetIcon
      name="ChevronLeft"
      size="xl"
      color={theme.colors.neutral.white}
      onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
    />
  );

  const arrowRightComponent = selectedImageIndex !== images.length - 1 && (
    <GetIcon
      name="ChevronRight"
      size="xl"
      color={theme.colors.neutral.white}
      onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
    />
  );

  return (
    <S.Wrapper>
      {selectedImageIndex !== null ? (
        <S.Background
          id="gallery-background"
          onClick={(e) => {
            e.stopPropagation();

            if (e.target.id === "gallery-background")
              setSelectedImageIndex(null);
          }}
        >
          {arrowLeftComponent}
          <S.Image
            src={images[selectedImageIndex].file_src}
            alt="Imagem do projeto"
          />
          {arrowRightComponent}

          <S.MobileArrows>
            {arrowLeftComponent}
            {arrowRightComponent}
          </S.MobileArrows>
        </S.Background>
      ) : null}

      {openModal ? (
        <GalleryUpload
          projectId={projectId}
          setOpenModal={setOpenModal}
          reload={() => getProjectImages(projectId)}
        />
      ) : null}

      <Form>
        <S.Section>
          {/* "Galeria" → "Álbum" (D18): renomeada em TODAS as superfícies —
              dois nomes para a mesma sessão confunde admin e proponente. */}
          <span>Álbum</span>
          <hr />
        </S.Section>

        <S.Buttons>
          <div />

          {!is_sponsor ? (
            <Button
              type="button"
              text="Adicionar ao Álbum"
              disabled={loading}
              onClick={() => setOpenModal(true)}
            />
          ) : null}
        </S.Buttons>

        {!items.length ? <span>Nenhum item cadastrado.</span> : null}

        <S.Gallery>
          {items.map((item) => {
            // Linha legada sem `type` é imagem (a coluna só passou a variar em
            // 03/09/2026).
            const type = item.type || "image";

            if (type === "image")
              return (
                <S.GalleryImage
                  key={item.id}
                  onClick={() =>
                    setSelectedImageIndex(
                      images.findIndex((image) => image.id === item.id),
                    )
                  }
                >
                  <img
                    src={item.file_src}
                    alt={item.name || "Imagem do projeto"}
                  />
                </S.GalleryImage>
              );

            if (type === "video")
              return (
                <S.GalleryVideo key={item.id}>
                  {/* Sem <track>: o vídeo é material do proponente e a
                      plataforma não tem legenda para oferecer. ⚠️ NÃO usar
                      `eslint-disable jsx-a11y/media-has-caption` aqui — o
                      plugin não está na config do build do CRA, e um disable de
                      regra inexistente FALHA o `npm run build` (o
                      `npx eslint` local não pega). */}
                  <video src={item.file_src} controls preload="metadata" />
                  <span>{item.name}</span>
                </S.GalleryVideo>
              );

            // Link externo e arquivo (PDF/Office/áudio/compactado): âncora. O
            // link abre no provedor; o arquivo, na URL assinada.
            return (
              <S.GalleryLink
                key={item.id}
                href={item.file_src}
                target="_blank"
                rel="noreferrer"
              >
                {/* ⚠️ O GetIcon importa `./Components/<name>` DINAMICAMENTE:
                    nome inexistente falha só em runtime, sem erro de build.
                    Não existe ícone "Link" no catálogo — "Display" (monitor) é
                    o mais próximo de mídia externa, e "File" serve o resto.
                    Catálogo completo em /icons. */}
                <GetIcon
                  name={type === "link" ? "Display" : "File"}
                  size="lg"
                  color={theme.colors.neutral.white}
                />
                <span>{item.name}</span>
              </S.GalleryLink>
            );
          })}
        </S.Gallery>
      </Form>
    </S.Wrapper>
  );
};

export default Gallery;
